import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Form, FormGroup, Label, Input } from 'reactstrap';
import { TextDropZone } from '../../DropZones/TextDropZone';
import { useStandardControlPSAApi } from './StandardControlPSAApi';
import { SubmitButton } from '../shared/SubmitButton';
import { AI_UTILITIES } from '../../../constants/aiUtilities';
import { useNotification } from '../../../components/index';
import { useRepeatProcessingLoader } from '../shared/hooks/useRepeatProcessingLoader';
import { CommentInput } from '../shared/CommentInput';
import { StartTimePicker } from '../shared/StartTimePicker';
import { useMountedRef } from '../../../components/hooks/useMountedRef';
import { useUserPreferences } from '../shared/hooks/useUserPreferences';

export const StandardControlPSALoader = () => {
    const history = useHistory();
    const location = useLocation();
    const { getPreferences, savePreferences } = useUserPreferences();

    const [files, setFiles] = useState([]);
    const [comment, setComment] = useState('');
    const [promptText, setPromptText] = useState('');
    const [startTime, setStartTime] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);

    // Режим повторной обработки
    const isRepeatMode = useRef(location.state?.isRepeatProcessing || false);
    const userInteracted = useRef(false);

    const { submitStandardControlPSARequest } = useStandardControlPSAApi();
    const {
        showError,
        showWarning
    } = useNotification();
    const isMounted = useMountedRef();

    const MAX_PROMPT_LENGTH = 1000;

    useEffect(() => {
        if (isRepeatMode.current) {
            return;
        }
        const loadPreferences = async () => {
            try {
                const prefs = await getPreferences(AI_UTILITIES.STANDARD_CONTROL_PSA.id);
                if (prefs.defaultPromptText && prefs.defaultPromptText.length <= MAX_PROMPT_LENGTH) {
                    setPromptText(prefs.defaultPromptText);
                } else {
                    setPromptText('');
                }
            } catch (error) {
                // Если предпочтений нет или ошибка – используем дефолты
                console.warn('Failed to load preferences', error);
            }
        };
        loadPreferences();
    }, []);

    useRepeatProcessingLoader({
        locationState: location.state,
        setFiles,
        setIsLoading,
        setIsInitializing,
        showError,
        isMounted,
        onLoaded: (state) => {
            setComment(state.comment || '');
            setPromptText(location.state.promptText || '');
        },
    });

    const saveCurrentPreferences = async () => {
        if (isRepeatMode.current && !userInteracted.current) return;
        try {
            await savePreferences(AI_UTILITIES.STANDARD_CONTROL_PSA.id, {
                promptText
            });
        } catch (error) {
            console.error('Failed to save preferences', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (files.length === 0) {
            showWarning("Пожалуйста, выберите файлы для обработки");
            return;
        }

        if (!promptText) {
            showWarning("Пожалуйста, введите промпт для обработки");
            return;
        }

        try {
            saveCurrentPreferences();
        }
        catch (error) {
            console.error(error);
        }

        setIsLoading(true);
        try {
            const startTimeString = startTime ? startTime.toISOString() : '';
            const { id, identifier } = await submitStandardControlPSARequest(files, promptText, comment, startTimeString);
            if (isMounted.current) {
                history.push({
                    pathname: `${AI_UTILITIES.STANDARD_CONTROL_PSA.path}/${identifier}`,
                    state: {
                        id,
                        fileNames: files.map(f => f.file.name),
                        comment: comment
                    }
                });
            }
        } catch (error) {
            showError(error.message);
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    };

    const handlePromptChange = (e) => {
        const value = e.target.value;
        if (value.length <= MAX_PROMPT_LENGTH) {
            setPromptText(value);
            userInteracted.current = true;
            isRepeatMode.current = false;
        }
    };

    const handleFilesChange = (newFiles) => {
        if (!isLoading && !isInitializing) {
            setFiles(newFiles);
        }
    };

    return (
        <Form onSubmit={handleSubmit} className="speakers-form-container">
            <FormGroup>
                <Label for="standardControlPSAPrompt" className="form-label standard-text">
                    Промпт
                </Label>
                <Input
                    type="textarea"
                    name="prompt"
                    id="standardControlPSAPrompt"
                    value={promptText}
                    onChange={handlePromptChange}
                    placeholder={`Опишите дополнительные требования к обработке (максимум ${MAX_PROMPT_LENGTH} символов)...`}
                    disabled={isLoading || isInitializing}
                    rows={3}
                    maxLength={MAX_PROMPT_LENGTH}
                    className="text-input standard-bg standard-text"
                />
                <div className={`counter mt-1 ${promptText.length > MAX_PROMPT_LENGTH ? 'text-danger' : 'text-muted'}`}>
                    {promptText.length}/{MAX_PROMPT_LENGTH}
                </div>
            </FormGroup>

            <CommentInput
                id="standardControlPSAComment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={isLoading || isInitializing}
            />

            <StartTimePicker
                value={startTime}
                onChange={setStartTime}
                disabled={isLoading || isInitializing}
            />

            <TextDropZone
                files={files}
                isLoading={isLoading || isInitializing}
                onFilesChange={handleFilesChange}
                showFormatSelector={true}
                maxFiles={1}
                maxFileSize={10 * 1024 * 1024}
                fileTypesDescription="PDF"
                accept={{ 'application/pdf': ['.pdf'] }}
                showTemplateSelector={false}
            />

            <SubmitButton
                isLoading={isLoading}
                text="Отправить на обработку ИИ"
                disabled={isLoading || isInitializing || files.length === 0}
            />
        </Form>
    );
};